import { DateTime } from "luxon";

import L from "leaflet";
import "leaflet/dist/leaflet.css"; // for styles
import "leaflet-control-geocoder/dist/Control.Geocoder.css";
import "leaflet-control-geocoder"; // imports the plugin

import iconUrl from "leaflet/dist/images/marker-icon.png";
import iconRetinaUrl from "leaflet/dist/images/marker-icon-2x.png";
import shadowUrl from "leaflet/dist/images/marker-shadow.png";
import Alpine from "alpinejs";

import { Modal } from "bootstrap";
import { waitForAlpineStore } from "../waitForAlpineStore";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl,
  iconUrl,
  shadowUrl,
});

export function Leaflet() {
  return {
    map: null,
    marker: null,
    initialized: false,
    coordinates: [13.4161, 75.2513],
    geocoder: null,

    mapModal: null,

    init() {
      const modalEl = document.getElementById("mapUIModal");
      this.mapModal = new Modal(modalEl); // get the active instance
    },

    copyFromLocationStore() {
      this.coordinates = [
        Alpine.store("location")?.latitude ?? 13.4198,
        Alpine.store("location")?.longitude ?? 75.2567,
      ];
    },

    saveToLocationStore(event) {
      Alpine.store("location").saveLocationFromOSM(
        this.coordinates[0],
        this.coordinates[1]
      );
      this.closeModal(event);
    },

    closeModal(event) {
      event?.target?.blur();
      queueMicrotask(() => this.mapModal.hide());
    },

    openModal() {
      if (!this.mapModal) {
        this.init();
      }

      if (!navigator.onLine) {
        Alpine.store("notification").showNotification(
          "Offline",
          "Map is unavailable while offline.",
          "warning",
          -1
        );
        return false;
      } else {
        this.mapModal.show();
      }
    },

    observeModal() {
      const modalEl = document.getElementById("mapUIModal");

      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (
            mutation.attributeName === "style" &&
            getComputedStyle(modalEl).display !== "none"
          ) {
            this.copyFromLocationStore(); // <-- ensure the working copy is fresh

            this.initializeIfNeeded();
          }
        });
      });

      observer.observe(modalEl, { attributes: true });
    },

    // initializeIfNeeded() {
    //   if (this.initialized) {
    //     this.map.invalidateSize(); // Needed if modal was hidden
    //     return;
    //   }

    //   this.$nextTick(() => {
    //     this.map = L.map("map").setView(this.coordinates, 12);

    //     L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
    //       maxZoom: 19,
    //       attribution: '&copy; OpenStreetMap contributors',
    //     }).addTo(this.map);

    //     this.marker = L.marker(this.coordinates).addTo(this.map);

    //     this.map.on("click", this.onMapClick.bind(this));

    //     this.initialized = true;

    //     setTimeout(() => {
    //       this.map.invalidateSize();
    //     }, 250);
    //   });
    // },

    initializeIfNeeded() {
      if (this.initialized) {
        this.copyFromLocationStore();
        this.marker.setLatLng(this.coordinates);
        this.map.setView(this.coordinates, 14);

        this.map.invalidateSize();

        return;
      }

      queueMicrotask(() => {
        this.map = L.map("map").setView(this.coordinates, 12);

        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          maxZoom: 19,
          attribution: "&copy; OpenStreetMap contributors",
          crossOrigin: true, // This makes the response non-opaque
        }).addTo(this.map);

        // Add marker
        this.marker = L.marker(this.coordinates).addTo(this.map);

        // Add click-to-place-marker functionality
        this.map.on("click", this.onMapClick.bind(this));

        //         // Initialize map WITHOUT the default attribution
        // const map = L.map('map', { attributionControl: false });

        // // Add a new attribution control with the pipe separator
        // L.control.attribution({ separator: ' | ' }).addTo(map);

        // // Assuming 'map' is your Leaflet map instance
        // this.map.attributionControl.addAttribution(
        //   'Geodata © <a rel="noopener noreferrer nofollow" href="https://opencagedata.com">OpenCage</a>'
        // );

        // map.attributionControl.addAttribution('| Search Fallback: <a href="https://photon.komoot.io" target="_blank">Photon</a>');

        // this.map.attributionControl.addAttribution(
        //   'Geodata © <a rel="noopener noreferrer nofollow" href="https://opencagedata.com">OpenCage</a>'
        // );

        // // this.map.attributionControl.addAttribution(
        // //   'Geodata © <a rel="noopener noreferrer nofollow" href="https://opencagedata.com">OpenCage</a>'
        // // );

        // this.map.attributionControl.addAttribution(
        //   'Search by <a href="https://nominatim.org">Nominatim</a>'
        // );

        // // 1. Credit the Copernicus Program (Data Source)
        // this.map.attributionControl.addAttribution(
        //   'Elevation & Weather: <a href="https://doi.org/10.5270/ESA-c5d3d65" target="_blank">Copernicus DEM</a>'
        // );

        // // 2. Credit Open-Meteo (The Service/API)
        // this.map.attributionControl.addAttribution(
        //   'Processed by <a href="https://open-meteo.com/" target="_blank">Open-Meteo</a>'
        // );

        // Attributions

        // Clean up the prefix
        this.map.attributionControl.setPrefix(false);
        const relAttr = 'rel="noopener noreferrer nofollow" target="_blank"';

        // this.map.attributionControl.addAttribution(
        //   `© <a href="https://www.openstreetmap.org/copyright" ${relAttr}>OpenStreetMap</a>`
        // );
        this.map.attributionControl.addAttribution(
          `Geodata: <a href="https://opencagedata.com" ${relAttr}>OpenCage</a>`
        );
        this.map.attributionControl.addAttribution(
          `<a href="https://photon.komoot.io" ${relAttr}>Photon</a>`
        );
        this.map.attributionControl.addAttribution(
          `Elevation: <a href="https://doi.org/10.5270/ESA-c5d3d65" ${relAttr}>Copernicus DEM</a>`
        );
        this.map.attributionControl.addAttribution(
          `<a href="https://open-meteo.com/" ${relAttr}>Open-Meteo</a>`
        );

        // // ✅ Add geocoder control
        // this.geocoder = L.Control.geocoder({
        //   defaultMarkGeocode: false,
        // }).addTo(this.map);

        // // ✅ When a location is selected via geocoder
        // this.geocoder.on("markgeocode", this.selectLocation.bind(this));

        const photonGeocoder = L.Control.Geocoder.photon(
          "https://photon.komoot.io/"
        );

        const Geocoder = new L.Control.Geocoder.Nominatim({
          serviceUrl: `${window.location.origin}/api/`,
          geocodingQueryParams: {
            format: "json",
          },
        });

        // this.geocoder.options.htmlTemplate = function (r) {
        //   if (r.address) {
        //     return [r.address.road, r.address.city, r.address.state]
        //       .filter(Boolean)
        //       .join(", ");
        //   }
        //   return r.display_name || r.name || "Unknown location";
        // };

        // 1. Create a "Dumb" Geocoder that just fetches from your API

        const customGeocoder = {
          abortController: null,

          geocode: async function (query) {
            if (this.abortController) this.abortController.abort();
            this.abortController = new AbortController();

            try {
              // The 500ms "Bump"
              await new Promise((resolve, reject) => {
                const t = setTimeout(resolve, 500);
                this.abortController.signal.addEventListener("abort", () => {
                  clearTimeout(t);
                  reject(new Error("Aborted"));
                });
              });

              const res = await fetch(
                `/api/search?q=${encodeURIComponent(query)}`,
                {
                  signal: this.abortController.signal,
                }
              );
              const data = await res.json();

              return data.map((r) => ({
                name: r.display_name,
                // The UI fix: Bold Name, Small Category/Location
                html: `
          <div style="line-height:1.2; padding: 2px;">
            <strong style="color:#111; font-size:13px;">${r.name}</strong> 
            <span style="font-size:10px; color:#888; text-transform:uppercase; margin-left:5px;">${r.category}</span>
            <br/>
            <small style="color:#666; font-size:11px;">${r.sub_label}</small>
          </div>
        `,
                center: L.latLng(parseFloat(r.lat), parseFloat(r.lon)),
                // Provide a default zoom bbox
                bbox: L.latLngBounds(
                  [parseFloat(r.lat) - 0.005, parseFloat(r.lon) - 0.005],
                  [parseFloat(r.lat) + 0.005, parseFloat(r.lon) + 0.005]
                ),
              }));
            } catch (err) {
              return [];
            }
          },

          suggest: function (query) {
            return this.geocode(query);
          },
        };

        // 2. Initialize the UI Control
        this.geocoder = L.Control.geocoder({
          geocoder: customGeocoder,
          minLength: 3, // Prevents hitting your limit on 1-letter typos
          queryMinLength: 3,
          placeholder: "Search places...",
          defaultMarkGeocode: false,
          suggestTimeout: 500, // Internal delay before calling your geocode function
          autoExpand: true, // Keeps the search bar open while typing
        }).addTo(this.map);

        // this.geocoder = L.Control.geocoder({
        //   geocoder,
        //   minLength: 3,
        //   defaultMarkGeocode: false,
        // }).addTo(this.map);

        // When a location is selected
        this.geocoder.on("markgeocode", (e) => {
          const latlng = e.geocode.center;
          this.coordinates = [latlng.lat, latlng.lng];
          this.marker.setLatLng(latlng);
          this.map.setView(latlng, 14);
        });

        this.initialized = true;

        // Fix rendering after modal open
        setTimeout(() => {
          this.map.invalidateSize();
        }, 250);
      });
    },

    // ✅ When user selects a location from search
    selectLocation(e) {
      const latlng = e.geocode.center;
      this.coordinates = [latlng.lat, latlng.lng];
      this.marker.setLatLng(latlng);
      this.map.setView(latlng, 14);
    },

    updateLocationonMap(lat, lng) {
      const modalEl = document.getElementById("mapUIModal");
      // Check if modal is actually visible to the user
      const isVisible = modalEl && modalEl.classList.contains("show");

      if (isVisible) {
        if (this.map) {
          // Safety to ensure map is ready.
          this.map.invalidateSize();

          this.marker.setLatLng([lat, lng]);

          // .flyTo provides a better UX than .setView for GPS updates
          this.map.flyTo([lat, lng], 16, {
            animate: true,
            duration: 1.5,
          });
        }
      }
    },

    onMapClick(e) {
      this.coordinates = [e.latlng.lat, e.latlng.lng];
      this.marker.setLatLng(this.coordinates);
    },

    useCurrentLocation() {
      if (!navigator.geolocation) {
        Alpine.store("notification").showNotification(
          "No GPS",
          "Geolocation is not supported on this device.",
          "warning",
          12
        );
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const lat = parseFloat(pos.coords.latitude.toFixed(6));
          const lng = parseFloat(pos.coords.longitude.toFixed(6));
          this.coordinates = [lat, lng];
          if (this.map) {
            this.map.setView([lat, lng], 13);
            if (this.marker) {
              this.marker.setLatLng([lat, lng]);
            }
          }
        },
        (err) => {
          Alpine.store("notification").showNotification(
            "No GPS",
            `Geolocation error: ${err.message}`,
            "danger",
            12
          );
          console.warn("Geolocation error:", err.message);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
        }
      );
    },
  };
}
